/**
 * useCanvasCoach Hook
 *
 * Manages canvas coach state and interactions.
 * Supports both chat-based coaching and direct canvas edit updates (hybrid mode).
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { useAIStore } from '@/stores/aiStore'
import {
  canvasCoachApi,
  type CanvasType,
  type CoachMode,
  type CanvasSectionCoverage,
  type CanvasCoachCoverageResponse,
  type CanvasCoachChatEvent,
  type CanvasCoachDirectEditUpdate,
} from '@/lib/api/client'

// ============================================================================
// TYPES
// ============================================================================

export type QuickActionType = 'add_examples' | 'skip' | 'show_more' | 'navigate_section' | 'text'

export interface QuickAction {
  label: string
  type: QuickActionType
  payload?: string // Examples to add, section ID to navigate, or text content
}

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  isStreaming?: boolean
  quickActions?: QuickAction[]
  sectionId?: string
}

export interface ExtractedEntity {
  type: string
  label: string
  description?: string
  sectionId?: string
  confidence: number
}

export interface CanvasCoachState {
  messages: ChatMessage[]
  sectionCoverage: CanvasCoachCoverageResponse | null
  isLoading: boolean
  isStreaming: boolean
  error: string | null
  mode: CoachMode
  quickActions: QuickAction[]
  sessionId: string | null
  suggestedNextSection: string | null
  suggestionMessage: string | null
  extractedEntities: ExtractedEntity[]
  awaitingResponse: boolean // True when AI asked a question and is waiting for user response
}

export interface UseCanvasCoachReturn {
  // State
  messages: ChatMessage[]
  sectionCoverage: CanvasCoachCoverageResponse | null
  isLoading: boolean
  isStreaming: boolean
  error: string | null
  mode: CoachMode
  quickActions: QuickAction[]
  sessionId: string | null
  suggestedNextSection: string | null
  suggestionMessage: string | null
  extractedEntities: ExtractedEntity[]
  awaitingResponse: boolean

  // Actions
  sendMessage: (content: string, currentSection?: string) => Promise<void>
  handleQuickAction: (action: QuickAction) => Promise<void>
  setMode: (mode: CoachMode) => void
  clearMessages: () => void
  loadCoverage: () => Promise<void>
  initializeSession: () => Promise<void>
  updateFromDirectEdit: (update: CanvasCoachDirectEditUpdate) => Promise<void>
  dismissSuggestion: () => void
  addEntityToCanvas: (entity: ExtractedEntity) => void
  dismissEntity: (entity: ExtractedEntity) => void
  clearEntities: () => void

  // Callbacks for parent component
  onAddExamples?: (examples: string) => void
  onNavigateSection?: (sectionId: string) => void

  // Config
  isConfigured: boolean
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Strip the ```json:meta ... ``` block from streamed content for display.
 * The metadata is parsed separately after streaming completes.
 */
function stripMetadataBlock(content: string): string {
  // Remove complete metadata blocks
  let cleaned = content.replace(/```json:meta[\s\S]*?```/g, '')
  // Also remove partial/incomplete metadata blocks at the end (during streaming)
  cleaned = cleaned.replace(/```json:meta[\s\S]*$/g, '')
  return cleaned.trim()
}

// ============================================================================
// HOOK
// ============================================================================

export function useCanvasCoach(
  accountId: string,
  projectId: string,
  instanceId: string | undefined,
  canvasType: CanvasType,
  /** Current canvas content - used to detect duplicates */
  canvasContent?: string
): UseCanvasCoachReturn {
  const { apiKey, coachingModel, conversationMode } = useAIStore()

  // State
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [sectionCoverage, setSectionCoverage] = useState<CanvasCoachCoverageResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<CoachMode>((conversationMode as CoachMode) || 'organic')
  const [quickActions, setQuickActions] = useState<QuickAction[]>([])
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [suggestedNextSection, setSuggestedNextSection] = useState<string | null>(null)
  const [suggestionMessage, setSuggestionMessage] = useState<string | null>(null)
  const [extractedEntities, setExtractedEntities] = useState<ExtractedEntity[]>([])
  const [awaitingResponse, setAwaitingResponse] = useState(false)

  // Refs
  const abortControllerRef = useRef<AbortController | null>(null)
  const currentSectionRef = useRef<string | null>(null) // Track current section for quickAction context

  // Check if configured
  const isConfigured = Boolean(apiKey && instanceId)

  // Initialize session (get or create)
  const initializeSession = useCallback(async () => {
    if (!instanceId) return

    try {
      setIsLoading(true)
      const response = await canvasCoachApi.getOrCreateSession(accountId, projectId, instanceId, canvasType)
      if (response.session) {
        setSessionId(response.session.id)
      }
    } catch (err) {
      console.error('Failed to initialize canvas coach session:', err)
      setError(err instanceof Error ? err.message : 'Failed to initialize session')
    } finally {
      setIsLoading(false)
    }
  }, [accountId, projectId, instanceId, canvasType])

  // Load section coverage
  const loadCoverage = useCallback(async () => {
    if (!instanceId) return

    try {
      const coverage = await canvasCoachApi.getCoverage(accountId, projectId, instanceId, mode)
      setSectionCoverage(coverage)
    } catch (err) {
      console.error('Failed to load section coverage:', err)
    }
  }, [accountId, projectId, instanceId, mode])

  // Initialize session on mount
  useEffect(() => {
    if (instanceId && !sessionId) {
      initializeSession()
    }
  }, [instanceId, sessionId, initializeSession])

  // Load coverage on mount, mode change, and after session init
  useEffect(() => {
    if (instanceId && sessionId) {
      loadCoverage()
    }
  }, [instanceId, sessionId, mode, loadCoverage])

  // Send a message
  const sendMessage = useCallback(
    async (content: string, currentSection?: string) => {
      if (!instanceId || !apiKey || !content.trim()) return

      // Track current section for quickAction context
      if (currentSection) {
        currentSectionRef.current = currentSection
      }

      setError(null)
      setIsLoading(true)
      setQuickActions([]) // Clear previous quick actions
      setAwaitingResponse(false) // User responded, no longer awaiting
      setSuggestedNextSection(null)
      setSuggestionMessage(null)

      // Add user message
      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
        sectionId: currentSection,
      }
      setMessages((prev) => [...prev, userMessage])

      // Add placeholder assistant message for streaming
      const assistantMessageId = crypto.randomUUID()
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      }
      setMessages((prev) => [...prev, assistantMessage])

      setIsStreaming(true)

      try {
        // Use streaming API
        let streamedContent = ''
        let finalQuickActions: QuickAction[] = []

        await canvasCoachApi.chatStream(
          accountId,
          projectId,
          instanceId,
          {
            message: content.trim(),
            mode,
            apiKey,
            model: coachingModel,
            currentSection,
            canvasContent, // Pass current canvas content to detect duplicates
          },
          (event: CanvasCoachChatEvent) => {
            switch (event.type) {
              case 'text':
                if (event.content) {
                  streamedContent += event.content
                  // Strip metadata block for display (it's parsed after streaming)
                  const displayContent = stripMetadataBlock(streamedContent)
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantMessageId
                        ? { ...m, content: displayContent }
                        : m
                    )
                  )
                }
                break

              case 'quick_actions':
                if (event.actions) {
                  finalQuickActions = event.actions
                }
                break

              case 'section_update':
                // Coverage will be refreshed after done event
                break

              case 'entity_extracted':
                // Collect extracted entities for display
                const extractedEntity: ExtractedEntity = {
                  type: event.entityType || 'Unknown',
                  label: event.label || '',
                  description: event.description,
                  sectionId: event.sectionId,
                  confidence: event.confidence || 0,
                }
                if (extractedEntity.label) {
                  setExtractedEntities(prev => {
                    // Avoid duplicates
                    const exists = prev.some(e => e.label === extractedEntity.label && e.sectionId === extractedEntity.sectionId)
                    if (exists) return prev
                    return [...prev, extractedEntity]
                  })
                }
                break

              case 'done':
                // Update coverage from final state
                if (event.coverage) {
                  loadCoverage()
                }
                break

              case 'error':
                setError(event.message || 'Unknown error')
                break
            }
          }
        )

        // Finalize message with cleaned content
        const finalContent = stripMetadataBlock(streamedContent)
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId
              ? {
                  ...m,
                  content: finalContent,
                  isStreaming: false,
                  quickActions: finalQuickActions.length > 0 ? finalQuickActions : undefined,
                }
              : m
          )
        )

        // Detect if AI is waiting for response (ends with a question)
        const responseEndsWithQuestion = finalContent.trim().endsWith('?')

        // Set awaitingResponse based on question detection
        // Progressive disclosure: don't show quick actions when AI is asking a question
        setAwaitingResponse(responseEndsWithQuestion)

        // Set quick actions for display (only if not awaiting response)
        if (finalQuickActions.length > 0 && !responseEndsWithQuestion) {
          setQuickActions(finalQuickActions)
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message'
        setError(errorMessage)

        // Update assistant message to show error
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId
              ? {
                  ...m,
                  content: `Sorry, something went wrong: ${errorMessage}`,
                  isStreaming: false,
                }
              : m
          )
        )
      } finally {
        setIsLoading(false)
        setIsStreaming(false)
      }
    },
    [instanceId, apiKey, accountId, projectId, mode, coachingModel, loadCoverage, canvasContent]
  )

  // Update from direct canvas edit (hybrid mode)
  const updateFromDirectEdit = useCallback(
    async (update: CanvasCoachDirectEditUpdate) => {
      if (!instanceId) return

      try {
        const response = await canvasCoachApi.updateFromDirectEdit(
          accountId,
          projectId,
          instanceId,
          update,
          mode
        )

        // Update local coverage state
        if (response.sectionCoverage && sectionCoverage) {
          setSectionCoverage({
            ...sectionCoverage,
            [update.sectionId]: response.sectionCoverage,
          })
        }

        // Set suggestion for next section (proactive suggest)
        if (response.suggestedNextSection) {
          setSuggestedNextSection(response.suggestedNextSection)
        }
        if (response.suggestionMessage) {
          setSuggestionMessage(response.suggestionMessage)
        }
      } catch (err) {
        console.error('Failed to update from direct edit:', err)
      }
    },
    [accountId, projectId, instanceId, mode, sectionCoverage]
  )

  // Dismiss suggestion
  const dismissSuggestion = useCallback(() => {
    setSuggestedNextSection(null)
    setSuggestionMessage(null)
  }, [])

  // Parse quickAction label to infer entity type and section
  const inferEntityTypeFromLabel = useCallback((label: string): { entityType: string; sectionId: string } => {
    const lowerLabel = label.toLowerCase()

    // Map common patterns to entity types and sections
    if (lowerLabel.includes('gain')) {
      return { entityType: 'Gain', sectionId: 'customer-gains' }
    }
    if (lowerLabel.includes('pain')) {
      return { entityType: 'Pain', sectionId: 'customer-pains' }
    }
    if (lowerLabel.includes('job')) {
      return { entityType: 'CustomerJob', sectionId: 'customer-jobs' }
    }
    if (lowerLabel.includes('segment') || lowerLabel.includes('customer')) {
      return { entityType: 'CustomerSegment', sectionId: 'customer-segments' }
    }
    if (lowerLabel.includes('revenue') || lowerLabel.includes('stream')) {
      return { entityType: 'RevenueStream', sectionId: 'revenue-streams' }
    }
    if (lowerLabel.includes('resource')) {
      return { entityType: 'KeyResource', sectionId: 'key-resources' }
    }
    if (lowerLabel.includes('activit')) {
      return { entityType: 'KeyActivity', sectionId: 'key-activities' }
    }
    if (lowerLabel.includes('partner')) {
      return { entityType: 'KeyPartnership', sectionId: 'key-partners' }
    }
    if (lowerLabel.includes('channel')) {
      return { entityType: 'Channel', sectionId: 'channels' }
    }
    if (lowerLabel.includes('relationship')) {
      return { entityType: 'CustomerRelationship', sectionId: 'customer-relationships' }
    }
    if (lowerLabel.includes('cost')) {
      return { entityType: 'CostStructure', sectionId: 'cost-structure' }
    }
    if (lowerLabel.includes('value') && lowerLabel.includes('prop')) {
      return { entityType: 'ValueProposition', sectionId: 'value-propositions' }
    }
    if (lowerLabel.includes('feature') || lowerLabel.includes('product') || lowerLabel.includes('service')) {
      return { entityType: 'Feature', sectionId: 'products-services' }
    }
    if (lowerLabel.includes('reliever')) {
      return { entityType: 'Feature', sectionId: 'pain-relievers' }
    }
    if (lowerLabel.includes('creator')) {
      return { entityType: 'Feature', sectionId: 'gain-creators' }
    }

    // Default fallback - use current section context if available
    const sectionId = currentSectionRef.current || 'customer-segments'
    return { entityType: 'Feature', sectionId }
  }, [])

  // Handle quick action button click
  const handleQuickAction = useCallback(
    async (action: QuickAction) => {
      setQuickActions([]) // Clear quick actions immediately

      switch (action.type) {
        case 'add_examples':
          // Parse payload into entities and add to extractedEntities
          if (action.payload) {
            const { entityType, sectionId } = inferEntityTypeFromLabel(action.label)

            // Parse pipe-separated items (format: "label - description" or just "label")
            const items = action.payload.split('|').map(item => item.trim()).filter(Boolean)

            const newEntities: ExtractedEntity[] = items.map(item => {
              // Try to split on " - " for label - description format
              const dashIndex = item.indexOf(' - ')
              let label: string
              let description: string | undefined

              if (dashIndex > 0) {
                label = item.substring(0, dashIndex).trim()
                description = item.substring(dashIndex + 3).trim()
              } else {
                label = item
                description = undefined
              }

              return {
                type: entityType,
                label,
                description,
                sectionId,
                confidence: 80, // High confidence for AI-suggested items
              }
            })

            // Add to extracted entities so they appear as pending on canvas
            setExtractedEntities(prev => [...prev, ...newEntities])
          }

          // Send confirmation message
          await sendMessage(`I'll add those to the canvas: ${action.label}`)
          break

        case 'show_more':
          // Ask for more examples
          await sendMessage('Show me more examples')
          break

        case 'navigate_section':
          // Navigate to a different section
          if (action.payload) {
            currentSectionRef.current = action.payload // Update section context
            await sendMessage(`Let's work on ${action.payload}`)
          }
          break

        case 'skip':
          // Skip to next suggestion
          await sendMessage('Skip to the next section')
          break

        case 'text':
        default:
          // Default: treat payload or label as message content
          await sendMessage(action.payload || action.label)
          break
      }
    },
    [sendMessage, inferEntityTypeFromLabel]
  )

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([])
    setQuickActions([])
    setError(null)
    setSuggestedNextSection(null)
    setSuggestionMessage(null)
    setExtractedEntities([])
    setAwaitingResponse(false)
  }, [])

  // Add entity to canvas (this callback will be provided by the component using the hook)
  // The actual canvas update is handled by the component since it has access to canvas state
  const addEntityToCanvas = useCallback((entity: ExtractedEntity) => {
    // Remove from extracted entities after adding to canvas
    setExtractedEntities(prev => prev.filter(e =>
      !(e.label === entity.label && e.sectionId === entity.sectionId)
    ))
  }, [])

  // Dismiss an entity without adding it
  const dismissEntity = useCallback((entity: ExtractedEntity) => {
    setExtractedEntities(prev => prev.filter(e =>
      !(e.label === entity.label && e.sectionId === entity.sectionId)
    ))
  }, [])

  // Clear all entities
  const clearEntities = useCallback(() => {
    setExtractedEntities([])
  }, [])

  return {
    // State
    messages,
    sectionCoverage,
    isLoading,
    isStreaming,
    error,
    mode,
    quickActions,
    sessionId,
    suggestedNextSection,
    suggestionMessage,
    extractedEntities,
    awaitingResponse,

    // Actions
    sendMessage,
    handleQuickAction,
    setMode,
    clearMessages,
    loadCoverage,
    initializeSession,
    updateFromDirectEdit,
    dismissSuggestion,
    addEntityToCanvas,
    dismissEntity,
    clearEntities,

    // Config
    isConfigured,
  }
}

export type { CanvasType, CoachMode, CanvasSectionCoverage, CanvasCoachCoverageResponse }
